<script setup lang="ts">
/**
 * Scheduled jobs (ADR 0071) — an assistant asked one fixed question on a
 * schedule, delivered into its recipients' DOIT AI history.
 *
 * Jobs are YAML in `config/jobs/`, written and reviewed by the DOIT AI team;
 * nothing here creates or edits one. What this page is for:
 *
 *   - **Will it run?** One status per job, most actionable first: Invalid
 *     (and why), Disabled in its file, Expired, Paused, Off platform-wide,
 *     Active — and when it fires next, in Copenhagen time.
 *   - **Did it run, and to whom?** Each run's status, cost and expectation
 *     result, and the effective access it ran with: who received it, who
 *     was dropped and why, the groups and keys it could read.
 *   - **Stop or nudge it.** Pause (with a reason), resume, run now — admin
 *     only, audited, the same functions as `python -m app.jobs`.
 *
 * Never the answer. A run's answer lives in the recipients' conversations;
 * the run record is metadata, and the backend strips the one quote of answer
 * text it can carry when the caller is a viewer (ADR 0072).
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type ScheduledJob,
  type ScheduledJobRun,
  type ScheduledJobsResponse,
  type ScheduledJobStatus,
  getScheduledJobRuns,
  getScheduledJobs,
  pauseScheduledJob,
  resumeScheduledJob,
  runScheduledJobNow,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { fmtDateTime, fmtIsoDate } from "../utils/datetime";
import SidePanel from "./SidePanel.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const data = ref<ScheduledJobsResponse | null>(null);
const loading = ref(false);
const error = ref("");

async function reload(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    data.value = await getScheduledJobs(props.devUser);
    if (selected.value) {
      const fresh = data.value.jobs.find(
        (j) => j.slug === selected.value!.slug,
      );
      selected.value = fresh ?? null;
      if (fresh) runs.value = fresh.runs;
    }
  } catch (e) {
    error.value = e instanceof AdminError ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}
onMounted(reload);

const STATUS: Record<
  ScheduledJobStatus,
  {
    text: string;
    tone: "ok" | "warn" | "bad" | "muted" | "neutral";
    why: string;
  }
> = {
  active: { text: "Active", tone: "ok", why: "Runs on its schedule." },
  off: {
    text: "Off globally",
    tone: "muted",
    why: "Scheduled jobs are switched off platform-wide.",
  },
  paused: {
    text: "Paused",
    tone: "warn",
    why: "Paused by hand or after three failed runs.",
  },
  expired: {
    text: "Expired",
    tone: "warn",
    why: "Its review_by date has passed; it pauses itself until renewed.",
  },
  disabled: {
    text: "Disabled",
    tone: "muted",
    why: "enabled: false in its file — switching it on is a reviewed change.",
  },
  invalid: {
    text: "Invalid",
    tone: "bad",
    why: "The file cannot run as it stands.",
  },
};

function runTone(s: string): "ok" | "warn" | "bad" | "info" | "neutral" {
  if (s === "succeeded") return "ok";
  if (s === "failed") return "bad";
  if (s === "skipped") return "warn";
  if (s === "running" || s === "pending") return "info";
  return "neutral";
}

function money(n: number | null | undefined): string {
  return n == null ? "—" : `${n.toFixed(2)} DKK`;
}

/** "Mon 12 Oct, 07:00" in Copenhagen — the zone every schedule is read in. */
function fireText(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    timeZone: data.value?.timezone ?? "Europe/Copenhagen",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function scopeText(v: string[] | "all-ingested"): string {
  return v === "all-ingested" ? "all ingested" : v.join(", ");
}

// ── Drawer ────────────────────────────────────────────────────────────────

const selected = ref<ScheduledJob | null>(null);
const drawerOpen = computed({
  get: () => selected.value !== null,
  set: (v: boolean) => {
    if (!v) selected.value = null;
  },
});
const runs = ref<ScheduledJobRun[]>([]);
const nextBefore = ref<number | null>(null);
const runsLoading = ref(false);
const openRun = ref<number | null>(null);
const pauseReason = ref("");
const busy = ref(false);

const PAGE = 20;

async function open(j: ScheduledJob): Promise<void> {
  selected.value = j;
  runs.value = j.runs;
  nextBefore.value = null;
  openRun.value = null;
  pauseReason.value = "";
  await loadRuns(true);
}

async function loadRuns(first = false): Promise<void> {
  if (!selected.value) return;
  runsLoading.value = true;
  try {
    const page = await getScheduledJobRuns(
      selected.value.slug,
      { limit: PAGE, before: first ? null : nextBefore.value },
      props.devUser,
    );
    runs.value = first ? page.runs : [...runs.value, ...page.runs];
    nextBefore.value = page.next_before;
  } catch (e) {
    fail(e, "Could not load the runs");
  } finally {
    runsLoading.value = false;
  }
}

function fail(e: unknown, title: string): void {
  toast.error(title, e instanceof AdminError ? e.message : String(e));
}

async function pause(): Promise<void> {
  const j = selected.value;
  if (!j || !pauseReason.value.trim()) return;
  busy.value = true;
  try {
    await pauseScheduledJob(j.slug, pauseReason.value.trim(), props.devUser);
    toast.success("Paused", `${j.name} will not run until it is resumed.`);
    pauseReason.value = "";
    await reload();
  } catch (e) {
    fail(e, "Could not pause the job");
  } finally {
    busy.value = false;
  }
}

async function resume(): Promise<void> {
  const j = selected.value;
  if (!j) return;
  busy.value = true;
  try {
    await resumeScheduledJob(j.slug, props.devUser);
    toast.success("Resumed", `${j.name} runs from its next fire.`);
    await reload();
  } catch (e) {
    fail(e, "Could not resume the job");
  } finally {
    busy.value = false;
  }
}

async function runNow(): Promise<void> {
  const j = selected.value;
  if (!j) return;
  const ok = await confirm({
    title: `Run “${j.name}” now?`,
    message:
      `A real run, queued for the next jobs-cron tick (within five minutes). ` +
      `If it passes, the answer is delivered to ${j.recipients.length} ` +
      `recipient(s) as a conversation in their history. It costs tokens.`,
    confirmLabel: "Run now",
  });
  if (!ok) return;
  busy.value = true;
  try {
    const r = await runScheduledJobNow(j.slug, props.devUser);
    toast.success("Queued", `Run #${r.run_id} runs on the next tick.`);
    await reload();
    await loadRuns(true);
  } catch (e) {
    fail(e, "Could not queue a run");
  } finally {
    busy.value = false;
  }
}

/** Why Run now cannot work, mirroring the backend's refusal — shown on the
 *  button instead of letting an admin click into a 409. */
const runNowBlocked = computed(() => {
  const j = selected.value;
  if (!j || !data.value) return "";
  if (!data.value.jobs_enabled)
    return "Scheduled jobs are switched off platform-wide.";
  if (data.value.cron_wired === false)
    return "This environment has no jobs-cron runner.";
  if (j.status === "invalid") return "The job is invalid.";
  if (j.status === "disabled") return "The job is disabled in its file.";
  if (j.status === "expired") return "The job's review_by date has passed.";
  return "";
});
</script>

<template>
  <div class="sched">
    <header class="sched__header">
      <h2>Scheduled jobs</h2>
      <button class="btn" :disabled="loading" @click="reload">Refresh</button>
    </header>
    <p class="muted small sched__lede">
      An assistant asked one fixed question on a schedule; the answer lands in
      each recipient's DOIT AI history. Jobs are defined as YAML in
      <code>config/jobs/</code> by the DOIT AI team and reviewed in a pull
      request — nothing here creates or edits one.
    </p>

    <div v-if="data && !data.jobs_enabled" class="sched__banner" role="status">
      <strong>Scheduled jobs are switched off platform-wide.</strong>
      No job runs and nothing queued is delivered while
      <code>JOBS_ENABLED</code> is false. They are turned on in the
      environment's Bicep parameters — <code>includeJobsCron=true</code> (the
      runner) and <code>jobsEnabled=true</code> (the switch) — by the DOIT AI
      team, after the first job has passed <code>just job-try</code>
      and its review.
    </div>
    <div
      v-else-if="data && data.cron_wired === false"
      class="sched__banner"
      role="status"
    >
      <strong>No runner in this environment.</strong>
      <code>includeJobsCron</code> is false, so no job runs here.
    </div>

    <p v-if="error" class="sched__error" role="alert">{{ error }}</p>

    <SkeletonRows v-if="loading && !data" :rows="4" />

    <div v-else-if="data && data.jobs.length === 0" class="sched__empty">
      <p><strong>No scheduled jobs yet.</strong></p>
      <p class="muted">
        Jobs are defined as YAML in <code>config/jobs/</code> by the DOIT AI
        team. The how-to is <code>docs/jobs/README.md</code> in the repository:
        try the question in chat,
        <code>just job-new &lt;slug&gt; &lt;assistant&gt;</code>,
        <code>just job-try &lt;slug&gt;</code>, then a pull request and
        push-config.
      </p>
    </div>

    <table v-else-if="data" class="admin-table sched__table">
      <thead>
        <tr>
          <th scope="col">Job</th>
          <th scope="col">Status</th>
          <th scope="col">Next run</th>
          <th scope="col">Last run</th>
          <th scope="col">Recipients</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="j in data.jobs" :key="j.slug">
          <td>
            <button class="link sched__name" @click="open(j)">
              {{ j.name }}
            </button>
            <div class="muted small">
              {{ j.slug
              }}<template v-if="j.assistant"> · {{ j.assistant }}</template>
            </div>
          </td>
          <td>
            <StatusChip
              :tone="STATUS[j.status].tone"
              dot
              :title="STATUS[j.status].why"
            >
              {{ STATUS[j.status].text }}
            </StatusChip>
          </td>
          <td>
            <span :class="!j.will_run && 'muted'">
              {{ fireText(j.next_fire) }}
            </span>
            <div v-if="!j.will_run && j.next_fire" class="muted small">
              will not run
            </div>
          </td>
          <td>
            <template v-if="j.runs.length">
              <StatusChip :tone="runTone(j.runs[0].status)">
                {{ j.runs[0].status }}
              </StatusChip>
              <div class="muted small">
                {{
                  fmtDateTime(j.runs[0].finished_at ?? j.runs[0].scheduled_for)
                }}
              </div>
            </template>
            <span v-else class="muted">never</span>
          </td>
          <td>{{ j.recipients.length }}</td>
        </tr>
      </tbody>
    </table>

    <SidePanel
      v-model="drawerOpen"
      :title="selected?.name ?? ''"
      :subtitle="selected ? `${selected.slug} · ${selected.source}` : ''"
    >
      <div v-if="selected" class="sched__detail">
        <section>
          <StatusChip :tone="STATUS[selected.status].tone" dot>
            {{ STATUS[selected.status].text }}
          </StatusChip>
          <span class="muted small"> {{ STATUS[selected.status].why }}</span>
          <ul v-if="selected.problems.length" class="sched__problems">
            <li v-for="p in selected.problems" :key="p">{{ p }}</li>
          </ul>
          <p v-if="selected.paused" class="small">
            Paused {{ fmtDateTime(selected.paused_at) }}:
            {{ selected.paused_reason || "no reason given" }}
          </p>
          <p v-if="selected.consecutive_failures" class="small">
            {{ selected.consecutive_failures }} failed run(s) in a row — three
            pause it.
          </p>
        </section>

        <section class="sched__actions" aria-label="Actions">
          <button
            v-write
            class="btn btn--primary"
            :disabled="busy || !!runNowBlocked"
            :title="runNowBlocked || 'Queue one real run for the next tick'"
            @click="runNow"
          >
            Run now
          </button>
          <button
            v-if="selected.paused"
            v-write
            class="btn"
            :disabled="busy"
            @click="resume"
          >
            Resume
          </button>
          <form v-else class="sched__pause" @submit.prevent="pause">
            <label class="sr-only" for="sched-pause-reason">
              Reason for pausing
            </label>
            <input
              id="sched-pause-reason"
              v-model="pauseReason"
              v-write
              maxlength="400"
              placeholder="Why pause it? (required)"
            />
            <button
              v-write
              type="submit"
              class="btn"
              :disabled="busy || !pauseReason.trim()"
            >
              Pause
            </button>
          </form>
          <p v-if="runNowBlocked" class="muted small sched__why">
            Run now: {{ runNowBlocked }}
          </p>
        </section>

        <section>
          <h3>What it is</h3>
          <dl class="sched__facts">
            <dt>Purpose</dt>
            <dd>{{ selected.purpose || "—" }}</dd>
            <dt>Assistant</dt>
            <dd>{{ selected.assistant || "—" }}</dd>
            <dt>Owner</dt>
            <dd>{{ selected.owner || "—" }}</dd>
            <dt>Asked for by</dt>
            <dd>{{ selected.requested_by || "—" }}</dd>
            <dt>Schedule</dt>
            <dd>
              <code>{{ selected.schedule || "—" }}</code>
              <span class="muted small"> ({{ data?.timezone }})</span>
              <div class="small">Next: {{ fireText(selected.next_fire) }}</div>
            </dd>
            <dt>Recipients</dt>
            <dd>{{ selected.recipients.join(", ") || "—" }}</dd>
            <dt>Declared access</dt>
            <dd v-if="selected.declared_access">
              <div>
                Groups:
                {{ selected.declared_access.groups.join(", ") || "none" }}
              </div>
              <div
                v-for="(v, k) in selected.declared_access.row_scopes"
                :key="k"
              >
                {{ k }}: {{ scopeText(v) }}
              </div>
            </dd>
            <dd v-else>—</dd>
            <dt>Cost cap</dt>
            <dd>{{ money(selected.max_cost_dkk) }} per run</dd>
            <dt>Review by</dt>
            <dd>{{ fmtIsoDate(selected.review_by) }}</dd>
          </dl>
        </section>

        <section v-if="selected.last_access">
          <h3>Effective access, last run</h3>
          <p class="muted small">
            The declared access, narrowed to what every recipient may see
            themselves.
          </p>
          <dl class="sched__facts">
            <dt>Received by</dt>
            <dd>
              <template v-if="selected.last_access.recipients.length">
                <div
                  v-for="r in selected.last_access.recipients"
                  :key="r.email"
                >
                  {{ r.email }} <span class="muted small">({{ r.via }})</span>
                </div>
              </template>
              <span v-else class="muted">nobody</span>
            </dd>
            <template v-if="selected.last_access.dropped.length">
              <dt>Dropped</dt>
              <dd>
                <div v-for="d in selected.last_access.dropped" :key="d.email">
                  {{ d.email }} — {{ d.reason }}
                </div>
              </dd>
            </template>
            <dt>Groups</dt>
            <dd>
              {{ selected.last_access.table_groups.join(", ") || "none" }}
            </dd>
            <dt>Row keys</dt>
            <dd>
              <div
                v-for="(keys, scope) in selected.last_access.row_keys"
                :key="scope"
              >
                {{ scope }}: {{ keys.join(", ") || "none" }}
              </div>
              <span
                v-if="!Object.keys(selected.last_access.row_keys).length"
                class="muted"
                >none declared</span
              >
            </dd>
          </dl>
        </section>

        <section>
          <h3>Runs</h3>
          <p v-if="!runs.length && !runsLoading" class="muted">No runs yet.</p>
          <ul class="sched__runs">
            <li v-for="r in runs" :key="r.id" class="sched__run">
              <button
                class="sched__run-head"
                :aria-expanded="openRun === r.id"
                @click="openRun = openRun === r.id ? null : r.id"
              >
                <StatusChip :tone="runTone(r.status)">{{
                  r.status
                }}</StatusChip>
                <span>#{{ r.id }}</span>
                <span class="muted small">
                  {{ fmtDateTime(r.scheduled_for) }} ·
                  {{
                    r.trigger === "manual"
                      ? `run now by ${r.requested_by}`
                      : "scheduled"
                  }}
                </span>
                <span class="small sched__cost">{{ money(r.cost_dkk) }}</span>
              </button>
              <div v-if="openRun === r.id" class="sched__run-body small">
                <p v-if="r.error">{{ r.error }}</p>
                <p>
                  Started {{ fmtDateTime(r.started_at) }} · finished
                  {{ fmtDateTime(r.finished_at) }}
                </p>
                <p v-if="r.expect">
                  Expectations:
                  {{ r.expect.passed ? "passed" : "failed" }}
                  <template v-if="r.expect.problems.length">
                    — {{ r.expect.problems.join("; ") }}
                  </template>
                </p>
                <p>
                  Delivered to {{ r.delivered }} recipient(s)<template
                    v-if="r.documents"
                  >
                    · {{ r.documents }} document(s)</template
                  >
                </p>
                <p v-if="r.workflows.length">
                  Workflows: {{ r.workflows.join(", ") }}
                </p>
                <p v-if="r.tools_called.length">
                  Tools called: {{ r.tools_called.join(", ") }}
                </p>
                <p v-if="r.access">
                  Access: to
                  {{
                    r.access.recipients.map((x) => x.email).join(", ") ||
                    "nobody"
                  }}
                  <template v-if="r.access.dropped.length">
                    · dropped
                    {{
                      r.access.dropped
                        .map((x) => `${x.email} (${x.reason})`)
                        .join(", ")
                    }}
                  </template>
                </p>
              </div>
            </li>
          </ul>
          <button
            v-if="nextBefore != null"
            class="btn btn--ghost"
            :disabled="runsLoading"
            @click="loadRuns()"
          >
            Older runs
          </button>
        </section>
      </div>
    </SidePanel>
  </div>
</template>

<style scoped>
.sched {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}
.sched__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.sched__header h2 {
  margin: 0;
}
.sched__lede {
  margin: 0;
  max-width: 70ch;
}
.sched__banner {
  padding: 0.6rem 0.8rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-warn);
  background: var(--color-warn-bg);
  color: var(--color-warn-ink);
  font-size: 0.9rem;
  line-height: 1.5;
}
.sched__error {
  color: var(--color-error);
  margin: 0;
}
.sched__empty {
  padding: var(--space-3);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-lg);
}
.sched__empty p {
  margin: 0 0 0.4rem;
}
.sched__name {
  font-weight: 600;
  text-align: left;
}
.sched__detail {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.sched__detail h3 {
  margin: 0 0 0.4rem;
  font-size: 0.95rem;
}
.sched__problems {
  margin: 0.4rem 0 0;
  padding-left: 1.2rem;
  color: var(--color-error);
  font-size: 0.88rem;
}
.sched__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
}
.sched__pause {
  display: flex;
  gap: var(--space-2);
  flex: 1 1 16rem;
  min-width: 0;
}
.sched__pause input {
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.sched__why {
  flex-basis: 100%;
  margin: 0;
}
.sched__facts {
  display: grid;
  grid-template-columns: minmax(7rem, max-content) 1fr;
  gap: 0.3rem 0.8rem;
  margin: 0;
  font-size: 0.9rem;
}
.sched__facts dt {
  color: var(--color-muted);
}
.sched__facts dd {
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}
.sched__runs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.sched__run {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.sched__run-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4rem 0.6rem;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.sched__run-head:focus-visible {
  outline: none;
  box-shadow: var(--ring);
  border-radius: var(--radius-md);
}
.sched__cost {
  margin-left: auto;
}
.sched__run-body {
  padding: 0 0.6rem 0.5rem;
  overflow-wrap: anywhere;
}
.sched__run-body p {
  margin: 0.25rem 0;
}
.small {
  font-size: 0.82rem;
}
.muted {
  color: var(--color-muted);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
}
</style>
